import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Card,
  CardContent,
  Modal,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import MasterLayout from "../../../masterLayout/MasterLayout";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import { baseUrl, counselorId } from "../../../helper/urls";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";

const PackageTableSection = ({
  heading = "h5",
  subheading,
  sectionLabel,
  description,
  columns,
  rows,
  onRequest,
}) => {
  const primaryColor = "#1e3a5f";

  return (
    <Box sx={{ mb: 6 }}>
      <Typography
        variant={heading}
        component={heading}
        align="center"
        sx={{ color: primaryColor, mt: 4, mb: 1, fontWeight: 700 }}
      >
        {subheading}
      </Typography>

      {description && (
        <Typography
          variant="body1"
          align="center"
          sx={{ maxWidth: 800, mx: "auto", mb: 3 }}
        >
          {description}
        </Typography>
      )}

      <TableContainer component={Paper} sx={{ maxWidth: 1000, mx: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    backgroundColor: "#e9eff6",
                    color: primaryColor,
                    fontWeight: 600,
                    textAlign: "center",
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx}>
                {columns.map((col) => (
                  <TableCell key={`${idx}-${col}`} align="center">
                    {col === "Request" ? (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          backgroundColor: primaryColor,
                          "&:hover": { backgroundColor: "#14324a" },
                          borderRadius: 2,
                          textTransform: "none",
                        }}
                        onClick={() =>
                          onRequest({ row, sectionLabel, subheading })
                        }
                      >
                        Request Now
                      </Button>
                    ) : (
                      row[col.toLowerCase().replace(/ /g, "")] ||
                      row[col] ||
                      "—"
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const StudentPackage = () => {
  const { token } = useSelector((state) => state.auth);
  const primaryColor = "#1e3a5f";

  const [openReq, setOpenReq] = useState(false);
  const [meta, setMeta] = useState(null);
  const [timeValue, setTimeValue] = useState(null);
  const [errors, setErrors] = useState({ time: false });
  const [loading, setLoading] = useState(false);
  const [packageDetailsData, setPackageDetailsData] = useState({});
  console.log("packageDetailsData: ", packageDetailsData);

  const handleOpenReqModal = (payload) => {
    setMeta(payload);
    setOpenReq(true);
  };
  const handleCloseReqModal = () => {
    setOpenReq(false);
    setTimeValue(null);
    setErrors({ time: false });
    setMeta(null);
  };

  const buildDescription = () => {
    if (!meta) return "";
    const { sectionLabel, subheading, row } = meta;
    const cleanSub = subheading.replace(/^[^\w]+\s*/, "").trim();

    if (sectionLabel === "Standard Counselors") {
      return `${sectionLabel}-${row.service || cleanSub}-${row.hours}`;
    }

    if (cleanSub === "Full Support Counseling Plans") {
      return `Master Consultants-${cleanSub}-${row.plan || row.plan_name}`;
    }

    return `Master Consultants-${cleanSub}-${row.hours}`;
  };

  const createBookingRequest = async () => {
    setLoading(true);
    const description = buildDescription();

    const startHourStr = timeValue?.split("-")[0];
    const localTime = new Date();
    if (startHourStr) {
      const match = startHourStr.match(/(\d+)(AM|PM)/);
      if (match) {
        let hour = parseInt(match[1], 10);
        const period = match[2];
        if (period === "PM" && hour !== 12) hour += 12;
        if (period === "AM" && hour === 12) hour = 0;
        localTime.setHours(hour, 0, 0, 0);
      }
    }
    const utcTime = localTime.toISOString();

    try {
      await axios.post(
        `${baseUrl}/api/booking_req/packageReq`,
        {
          counselorId: counselorId,
          call_time: timeValue,
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          utcTime,
          description,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      Swal.fire("Success", `Request created successfully`, "success");
      handleCloseReqModal();
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: "Failed to create booking request",
        icon: "error",
        didOpen: () => {
          Swal.getContainer().style.zIndex = "2000";
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    const newErrors = { time: !timeValue };
    setErrors(newErrors);
    if (!newErrors.time) createBookingRequest();
  };

  const packageDetailApi = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/dyn/package/plan/all/packageDetails`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setPackageDetailsData(response.data.data || {});
    } catch (error) {
      toast.error("Failed to fetch package details.");
    }
  };

  useEffect(() => {
    packageDetailApi();
  }, []);

  return (
    <>
      <ToastContainer />
      <MasterLayout>
        {Object.entries(packageDetailsData || {}).map(
          ([sectionLabel, sectionItems], index) => (
            <Card key={index} sx={{ mt: 4 }}>
              <CardContent>
                <Typography
                  variant="h3"
                  component="h3"
                  align="center"
                  sx={{ fontWeight: "bold", color: primaryColor, mb: 3, mt: 3 }}
                >
                  {sectionLabel === "Standard Counselors"
                    ? "Packages of Admissions Counseling"
                    : ""}
                </Typography>
                <Typography
                  variant="h4"
                  component="h4"
                  align="center"
                  sx={{ fontWeight: "bold", color: primaryColor, mb: 3, mt: 3 }}
                >
                  🎓 {sectionLabel}
                </Typography>

                {sectionItems.map((item, idx) => {
                  let columns = [];
                  let rows = [];

                  const cleanSub = item.sub_title?.toLowerCase() || "";

                  if (cleanSub.includes("full support")) {
                    columns = ["Plan", "Description", "Flat Fee", "Request"];
                    rows = item.plans.map((p) => ({
                      plan: p.plan_name || "—",
                      description: p.description || "—",
                      flatfee: p.flat_fee ? `$${p.flat_fee}` : "—",
                    }));
                  } else {
                    columns = [
                      "Service",
                      "Hours",
                      "Hourly Rate",
                      "Total Price",
                      "Savings",
                      "Request",
                    ];
                    rows = item.plans.map((p) => ({
                      service: p.service || item.sub_title,
                      hours: `${p.hours} hrs`,
                      hourlyrate: `$${p.hourly_rate}`,
                      totalprice: `$${p.total_price}`,
                      savings: p.savings ? `Save $${p.savings}` : "—",
                    }));
                  }

                  return (
                    <PackageTableSection
                      key={idx}
                      heading="h5"
                      sectionLabel={sectionLabel}
                      subheading={`${
                        sectionLabel == "Standard Counselors"
                          ? ""
                          : item.sub_title == "Essay Counseling"
                          ? `📝 ${item.sub_title}`
                          : item.sub_title == "Full Support Counseling Plans"
                          ? `📘 ${item.sub_title}`
                          : item.sub_title ==
                            "Comprehensive Service (Essay, College Advising)"
                          ? `🎯 ${item.sub_title}`
                          : `🎓 ${item.sub_title}`
                      }`}
                      description={item.description || ""}
                      columns={columns}
                      rows={rows}
                      onRequest={handleOpenReqModal}
                    />
                  );
                })}
              </CardContent>
            </Card>
          )
        )}
        {/* Modal */}
        <Modal open={openReq} onClose={handleCloseReqModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Select preferred time
            </Typography>
            <Autocomplete
              fullWidth
              options={["9AM-12PM", "12PM-3PM", "3PM-6PM", "6PM-9PM"]}
              value={timeValue}
              onChange={(_, val) => setTimeValue(val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Best time to call"
                  error={errors.time}
                  helperText={errors.time ? "Required" : ""}
                />
              )}
            />
            <Button
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, backgroundColor: "#124178" }}
              onClick={handleSubmit}
            >
              {loading ? <CircularProgress size={24} /> : "Submit"}
            </Button>
          </Box>
        </Modal>
      </MasterLayout>
    </>
  );
};

export default StudentPackage;
